import React, { useState, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  Visibility as ViewIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
} from '@material-ui/icons'
import { Button, Box } from '@mui/material'
import dayjs from 'dayjs'
import {
  AdminPageWrapper,
  AdminSearchBar,
  AdminTable,
  SearchFilters,
  AdminTableColumn,
} from '../AdminComponents'
import {
  OneOnOneCoachingEvent,
  useGetAdminOneOnOneCoachingEventsQuery,
} from '../../../../Generated/graphql-types'

const OneOnOneCoachingAdmin: React.FC = () => {
  const history = useHistory()
  const location = useLocation()

  const params = new URLSearchParams(location.search)
  const startDateFromUrl = params.get('startDate')
  const endDateFromUrl = params.get('endDate')

  // Initialize search filters with URL params if present
  const [searchFilters, setSearchFilters] = useState<SearchFilters>(() => {
    const filters: SearchFilters = {}
    if (startDateFromUrl) {
      filters.startDate = startDateFromUrl
    }
    if (endDateFromUrl) {
      filters.endDate = endDateFromUrl
    }
    // Default to excluding deleted events
    filters.excludeDeleted = true
    return filters
  })

  // State for pagination
  const [currentPage, setCurrentPage] = useState(0)
  const pageSize = 20

  // Build query variables
  const queryVariables = useMemo(
    () => ({
      id: searchFilters.id
        ? parseInt(searchFilters.id as string, 10)
        : undefined,
      startDate: searchFilters.startDate
        ? dayjs(searchFilters.startDate as string).toISOString()
        : undefined,
      endDate: searchFilters.endDate
        ? dayjs(searchFilters.endDate as string).toISOString()
        : undefined,
      limit: pageSize,
      offset: currentPage * pageSize,
      excludeDeleted: searchFilters.excludeDeleted as boolean,
    }),
    [searchFilters, currentPage]
  )

  const { data, loading, error } = useGetAdminOneOnOneCoachingEventsQuery({
    variables: queryVariables,
    fetchPolicy: 'no-cache',
  })

  const searchFields = [
    {
      key: 'id',
      label: 'Event ID',
      placeholder: 'Enter event ID',
      type: 'number' as const,
    },
    {
      key: 'startDate',
      label: 'Start Date',
      placeholder: 'Enter start date',
      type: 'date' as const,
    },
    {
      key: 'endDate',
      label: 'End Date',
      placeholder: 'Enter end date',
      type: 'date' as const,
    },
    {
      key: 'excludeDeleted',
      label: 'Exclude deleted events',
      type: 'checkbox' as const,
    },
  ]

  const columns: AdminTableColumn<OneOnOneCoachingEvent>[] = [
    {
      key: 'id',
      label: 'ID',
      render: (value) => `#${value}`,
    },
    {
      key: 'hubspotId',
      label: 'Hubspot ID',
      render: (value) => value || '-',
    },
    {
      key: 'meetingTitle',
      label: 'Meeting Title',
      render: (value) => value || '-',
    },
    {
      key: 'contactName',
      label: 'User',
      render: (value) => value || '-',
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => value,
      sortable: true,
    },
    {
      key: 'startsAt',
      label: 'Starts',
      render: (value) => dayjs(value).format('LLL'),
    },
    {
      key: 'endsAt',
      label: 'Ends',
      render: (value) => dayjs(value).format('LLL'),
    },
    {
      key: 'activityType',
      label: 'Meeting Type',
      render: (value) => value || '-',
    },
    {
      key: 'deletedAt',
      label: 'Deleted',
      render: (value) => (value ? 'Yes' : 'No'),
    },
  ]

  const events = useMemo(
    () => data?.adminOneOnOneCoachingEvents?.events.filter(Boolean) || [],
    [data?.adminOneOnOneCoachingEvents?.events]
  )

  const totalCount = data?.adminOneOnOneCoachingEvents?.total || 0

  const handleRowClick = (event: OneOnOneCoachingEvent) => {
    history.push(`/internal/self-serve/one-on-one-coaching/${event.id}`)
  }

  const handleSearch = React.useCallback((filters: SearchFilters) => {
    setSearchFilters(filters)
    setCurrentPage(0) // Reset to first page when searching
  }, [])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const clearUrlFilters = () => {
    history.push('/internal/self-serve/one-on-one-coaching-admin')
    setSearchFilters({ excludeDeleted: true })
  }

  if (error) {
    return (
      <AdminPageWrapper title='1:1 Coaching Administration'>
        <div>Error loading events: {error.message}</div>
      </AdminPageWrapper>
    )
  }

  const getSubtitle = () => {
    let subtitle = 'View and manage all 1:1 coaching calls in the system'
    if (searchFilters.excludeDeleted) {
      subtitle += ' (excluding deleted events)'
    }
    return subtitle
  }

  return (
    <AdminPageWrapper
      title='1:1 Coaching Administration'
      subtitle={getSubtitle()}
      actions={
        <Box display='flex' gap={2}>
          <Button
            variant='contained'
            size='small'
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/internal/self-serve')}
            style={{
              backgroundColor: '#6B46C1',
              color: 'white',
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            Back to Self Serve
          </Button>
          {(startDateFromUrl ||
            endDateFromUrl ||
            !searchFilters.excludeDeleted) && (
            <Button
              variant='outlined'
              size='small'
              startIcon={<ClearIcon />}
              onClick={clearUrlFilters}
            >
              Clear Filters
            </Button>
          )}
        </Box>
      }
    >
      <AdminSearchBar
        fields={searchFields}
        onSearch={handleSearch}
        initialFilters={searchFilters}
      />

      <AdminTable<OneOnOneCoachingEvent>
        columns={columns}
        data={events}
        loading={loading}
        onRowClick={handleRowClick}
        emptyMessage='No events found'
        pageSize={pageSize}
        totalCount={totalCount}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        serverSidePagination
        actions={[
          {
            icon: <ViewIcon />,
            tooltip: 'View details',
            onClick: handleRowClick,
          },
        ]}
      />
    </AdminPageWrapper>
  )
}

export default OneOnOneCoachingAdmin
